<template>
  <div ref="chart" class="echarts-waterfall-chart" role="img" :aria-label="ariaLabel" />
</template>

<script>
import echarts, { chartAnimationDuration } from './echarts-core'

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const AXIS_FORMATTER = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0
})

export default {
  name: 'EChartsWaterfallChart',
  props: {
    grossSales: { type: Number, default: 0 },
    discounts: { type: Number, default: 0 },
    refunds: { type: Number, default: 0 },
    netSales: { type: Number, default: 0 },
    ariaLabel: { type: String, default: 'Gross-to-net sales waterfall' },
    isDark: { type: Boolean, default: false }
  },
  data() {
    return { chart: null, resizeObserver: null }
  },
  mounted() {
    this.$nextTick(this.initChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.refresh)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.chart) this.chart.dispose()
  },
  watch: {
    grossSales() { this.updateChart() },
    discounts() { this.updateChart() },
    refunds() { this.updateChart() },
    netSales() { this.updateChart() },
    isDark() { this.updateChart() }
  },
  methods: {
    initChart() {
      if (!this.$refs.chart || this.chart) return
      this.chart = echarts.init(this.$refs.chart, null, { renderer: 'canvas' })
      window.addEventListener('resize', this.refresh)
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.refresh)
        this.resizeObserver.observe(this.$refs.chart)
      }
      this.updateChart()
    },
    refresh() {
      this.$nextTick(() => this.chart && this.chart.resize())
    },
    formatCurrency(value) {
      return CURRENCY_FORMATTER.format(Number(value) || 0)
    },
    buildWaterfallData() {
      const gross = Math.max(0, Number(this.grossSales) || 0)
      const discounts = Math.max(0, Number(this.discounts) || 0)
      const refunds = Math.max(0, Number(this.refunds) || 0)
      const calculatedNet = Math.max(0, gross - discounts - refunds)
      const net = Math.max(0, Number(this.netSales) || calculatedNet)
      const afterDiscounts = Math.max(0, gross - discounts)
      const deductions = Math.max(0, gross - net)
      const focusedPadding = Math.max(gross * 0.12, deductions * 0.5, 1)

      return {
        categories: ['Gross sales', 'Discounts', 'Refunds', 'Net sales'],
        help: [0, afterDiscounts, net, 0],
        anchors: [gross, '-', '-', net],
        deductions: ['-', discounts, refunds, '-'],
        axisMin: Math.max(0, net - focusedPadding),
        gross,
        discounts,
        refunds,
        net
      }
    },
    updateChart() {
      if (!this.chart) return this.$nextTick(this.initChart)
      const data = this.buildWaterfallData()
      const textColor = this.isDark ? 'rgba(255,255,255,0.74)' : 'rgba(0,0,0,0.62)'
      const gridColor = this.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(13,45,44,0.08)'
      const anchorColor = this.isDark ? '#78cfa3' : '#0d2d2c'
      const deductionColor = this.isDark ? '#e0b476' : '#c18b45'
      const helperColor = 'rgba(0,0,0,0)'

      this.chart.setOption({
        animationDuration: chartAnimationDuration(320),
        grid: { top: 34, right: 26, bottom: 44, left: 18, containLabel: true },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          renderMode: 'richText',
          confine: true,
          backgroundColor: this.isDark ? 'rgba(35,35,35,0.96)' : 'rgba(20,20,20,0.9)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 12 },
          formatter: params => {
            const category = params?.[0]?.axisValue || ''
            const values = [data.gross, -data.discounts, -data.refunds, data.net]
            const index = data.categories.indexOf(category)
            const value = values[index] || 0
            const prefix = index === 1 || index === 2 ? '−' : ''
            return `${category}\n${prefix}${this.formatCurrency(Math.abs(value))}`
          }
        },
        xAxis: {
          type: 'category',
          data: data.categories,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: gridColor } },
          axisLabel: { color: textColor, fontSize: 11, fontWeight: 600, margin: 14 }
        },
        yAxis: {
          type: 'value',
          min: data.axisMin,
          axisLabel: { color: textColor, formatter: value => AXIS_FORMATTER.format(value) },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: gridColor } }
        },
        series: [
          {
            name: 'Offset',
            type: 'bar',
            stack: 'bridge',
            silent: true,
            itemStyle: { color: helperColor, borderColor: helperColor },
            emphasis: { disabled: true },
            data: data.help
          },
          {
            name: 'Sales total',
            type: 'bar',
            stack: 'bridge',
            barMaxWidth: 68,
            itemStyle: { color: anchorColor, borderRadius: [5, 5, 0, 0] },
            label: {
              show: true,
              position: 'top',
              color: this.isDark ? '#e8fff2' : '#0d2d2c',
              fontWeight: 800,
              formatter: point => this.formatCurrency(point.value)
            },
            data: data.anchors
          },
          {
            name: 'Deductions',
            type: 'bar',
            stack: 'bridge',
            barMaxWidth: 68,
            itemStyle: { color: deductionColor, borderRadius: [5, 5, 0, 0] },
            label: {
              show: true,
              position: 'top',
              color: this.isDark ? '#f5d7a8' : '#785323',
              fontWeight: 800,
              formatter: point => `−${this.formatCurrency(point.value)}`
            },
            data: data.deductions
          }
        ]
      }, true)
      this.refresh()
    }
  }
}
</script>

<style scoped>
.echarts-waterfall-chart { width: 100%; height: 100%; min-height: 300px; }
</style>
